import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

const CATEGORIES = [
  { key: "branding", label: "Branding" },
  { key: "system", label: "System" },
  { key: "mail", label: "Mail (SMTP)" },
  { key: "discord", label: "Discord" },
  { key: "security", label: "Security & Browser-Access" },
  { key: "quotas", label: "Quotas (free tier)" },
  { key: "billing", label: "Billing modes" },
  { key: "payment", label: "Payment providers" },
];

export default function AdminSettingsPage() {
  const { t } = useT();
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api("/admin/settings").then((r) => setSettings(r.settings)).catch((err) => toast.error(err.message));
  }, []);

  if (!settings) return <div className="loading">Loading settings</div>;

  const grouped = {};
  for (const s of settings) {
    const cat = s.key.split(".")[0];
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  }

  function setEdit(key, value) {
    setEdits((e) => ({ ...e, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await api("/admin/settings", {
        method: "PUT",
        body: { updates: edits },
      });
      setSettings(res.settings);
      setEdits({});
      toast.success(`Updated ${res.updated.length} settings.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">Settings</h1>
        </div>
        <div className="ph-meta">
          {Object.keys(edits).length > 0 && (
            <button className="btn primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : `Save ${Object.keys(edits).length} change${Object.keys(edits).length === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </div>

      {message?.ok && <div className="chip ok" style={{ marginBottom: 18 }}>{message.ok}</div>}
      {message?.error && <div className="error-line">{message.error}</div>}

      {CATEGORIES.map((cat) => {
        const items = grouped[cat.key];
        if (!items) return null;
        return (
          <div key={cat.key} className="section">
            <div className="section-head">
              <h2>{cat.label}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {cat.key === "mail" && <TestButton label="Test mail" onTest={() => testMail(toast)} />}
                {cat.key === "discord" && <TestButton label="Test webhook" onTest={() => testDiscordWebhook(toast)} />}
                <span className="chip">{items.length}</span>
              </div>
            </div>
            <div className="section-body">
              {items.map((s) => (
                <SettingRow
                  key={s.key}
                  setting={s}
                  value={edits[s.key] ?? s.value}
                  changed={s.key in edits}
                  onChange={(v) => setEdit(s.key, v)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function TestButton({ label, onTest }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn ghost"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try { await onTest(); } finally { setBusy(false); }
      }}
      style={{ fontSize: 11, padding: "5px 10px" }}
    >
      {busy ? "Sending…" : label}
    </button>
  );
}

async function testMail(toast) {
  try {
    const r = await api("/discord/admin/test-mail", { method: "POST", body: {} });
    toast.success(`Test mail sent to ${r.sent_to}`);
  } catch (err) {
    toast.error("Mail test failed: " + err.message);
  }
}

async function testDiscordWebhook(toast) {
  try {
    await api("/discord/admin/test-webhook", { method: "POST", body: {} });
    toast.success("Test webhook sent — check your Discord channel");
  } catch (err) {
    toast.error("Webhook test failed: " + err.message);
  }
}

function SettingRow({ setting, value, changed, onChange }) {
  const labelText = setting.key.split(".").pop().replace(/_/g, " ");

  return (
    <div className="setting-row">
      <div className="label-block">
        <div className="lbl" style={{ textTransform: "capitalize" }}>
          {labelText}
          {changed && (
            <span className="chip acc" style={{ marginLeft: 8, fontSize: 9 }}>
              modified
            </span>
          )}
        </div>
        <div className="key">{setting.key}</div>
      </div>
      <div>
        {setting.value_type === "boolean" ? (
          <div
            className={"toggle " + (toBool(value) ? "on" : "")}
            onClick={() => onChange(!toBool(value))}
            role="switch"
            aria-checked={toBool(value)}
          >
            <div className="knob" />
          </div>
        ) : setting.value_type === "secret" ? (
          <input
            type="text"
            value={value || ""}
            placeholder={setting.has_value ? "(stored, click to replace)" : ""}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: "100%",
              background: "var(--bg)",
              border: "1px solid var(--line)",
              color: "var(--text)",
              padding: "9px 12px",
              borderRadius: "var(--r)",
              fontFamily: "var(--mono)",
              fontSize: 12,
              outline: "none",
            }}
          />
        ) : (
          <input
            type={setting.value_type === "number" ? "number" : "text"}
            value={value ?? ""}
            onChange={(e) => onChange(setting.value_type === "number" ? Number(e.target.value) : e.target.value)}
            style={{
              width: "100%",
              background: "var(--bg)",
              border: "1px solid var(--line)",
              color: "var(--text)",
              padding: "9px 12px",
              borderRadius: "var(--r)",
              fontFamily: "var(--mono)",
              fontSize: 12,
              outline: "none",
            }}
          />
        )}
      </div>
    </div>
  );
}

function toBool(v) {
  if (typeof v === "boolean") return v;
  return v === "true" || v === true || v === "1" || v === 1;
}
